import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { queryClient } from 'App';
import { StartMeasurementTypes } from 'types/MeasurementTypes';
import { mutateWithOffline } from 'utils/mutateWithOffline';
import useGetMeasurementTypes from './GetMeasurementTypesHook';
import {
  addOfflineMeasurement,
  getOfflineMeasurements,
  getOfflineMeasurementByUuid,
} from 'hooks/useOfflineMeasurements';
import { getMeasurementPageCount } from 'utils/measurementPageCount';

type CachedMeasurementListItem = {
  uuid: string;
  typeOfMeasurement?: string;
  typeOfMeasurementUuid?: string | null;
  lastFinishedPage?: number;
  pageCount?: number;
  isFinished?: boolean | number;
  measurementLabel?: string;
  roomName?: string | null;
};

const getServerMeasurementsFromOfflineApiCache = (orderUuid: string | null) => {
  if (!orderUuid) return null;
  try {
    const raw = localStorage.getItem('offlineApiCache');
    if (!raw) return null;
    const cache = JSON.parse(raw) as Record<string, { data: any; timestamp: number; url: string }>;
    const keysToTry = [
      `measurement/list?orderUuid=${orderUuid}`,
      `/measurement/list?orderUuid=${orderUuid}`,
    ];

    for (const k of keysToTry) {
      const hit = cache?.[k]?.data;
      if (hit) return hit;
    }
    return null;
  } catch {
    return null;
  }
};

const useStartMeasurement = (
  options?: UseMutationOptions<any, Error, StartMeasurementTypes>
) => {
  const { data: measurementTypes } = useGetMeasurementTypes(false);

  return useMutation({
    ...options,
    mutationKey: ['startMeasurement'],
    mutationFn: async ({typeOfMeasurementUuid, orderUuid, measurementUuid}: StartMeasurementTypes) => {
      return mutateWithOffline<any>(
        'POST',
        '/measurement',
        {
          orderUuid,
          typeOfMeasurementUuid,
          measurementUuid,
        },
        {
          entityType: 'measurement',
          simulateResponse: ({ tempId }) => {
            const now = new Date().toISOString();
            const measurementType = measurementTypes?.find(
              (m) => m.value === typeOfMeasurementUuid
            );

            // OFFLINE COPY: jeśli ktoś próbuje utworzyć drugi raz pomiar tego samego typu
            // w ramach tego samego zamówienia, to backend zwraca istniejący pomiar z isCopy=true.
            // Robimy to samo offline, żeby UI mogło przekierować użytkownika do dalszego kroku
            // na podstawie lastFinishedPage.
            if (orderUuid && measurementType) {
              const existing = getOfflineMeasurements(orderUuid).find(
                (m) => m.typeOfMeasurement === measurementType.type
              );

              if (existing) {
                const pageCountExisting =
                  typeof existing.pageCount === 'number' && existing.pageCount > 0
                    ? existing.pageCount
                    : getMeasurementPageCount(existing.typeOfMeasurement);

                const isFinished = Boolean(existing.isFinished);

                // Jeśli poprzedni pomiar tego typu jest ukończony, user chce utworzyć
                // kolejny pomiar tego samego typu (COPY) — ale jako NOWY rekord, z nowym uuid.
                // Startujemy od razu na ostatniej stronie (czyli lastFinishedPage = pageCount-1),
                // a ostatni krok ma być pusty.
                if (isFinished) {
                  const newUuid = tempId;
                  const lastFinishedPage = Math.max(1, pageCountExisting - 1);

                  const newMeasurement = {
                    ...existing,
                    uuid: newUuid,
                    createdAt: now,
                    updatedAt: now,
                    isCopy: 1,
                    isFinished: 0,
                    roomName: null,
                    lastFinishedPage,
                    pageCount: pageCountExisting,
                    isOfflineOnly: true,
                  };

                  addOfflineMeasurement(newMeasurement);

                  return {
                    status: 'success',
                    message: 'Measurement created',
                    measurement: {
                      ...newMeasurement,
                      // backend zwraca boolean
                      isCopy: true,
                    },
                  };
                }

                // Jeśli istniejący pomiar nie jest ukończony, zachowujemy poprzednie zachowanie:
                // nie tworzymy nowego, tylko zwracamy istniejący i pozwalamy kontynuować.
                return {
                  status: 'success',
                  message: 'Measurement created',
                  measurement: {
                    ...existing,
                    isCopy: true,
                  },
                };
              }

              // Jeśli nie mamy tego pomiaru w localStorage, ale user mógł go utworzyć ONLINE
              // wcześniej, to traktujemy to jako bazę do copy.
              // 1) próbujemy z offlineApiCache (działa nawet po odświeżeniu, jeśli była synchronizacja)
              // 2) fallback na React Query cache (działa tylko w tej sesji)
              const offlineApiCached = getServerMeasurementsFromOfflineApiCache(orderUuid) as
                | { measurements?: CachedMeasurementListItem[] }
                | null;

              const rqCached = queryClient.getQueryData(['measurements', orderUuid]) as
                | CachedMeasurementListItem[]
                | undefined;

              const serverList: CachedMeasurementListItem[] = Array.isArray(
                offlineApiCached?.measurements
              )
                ? (offlineApiCached!.measurements as CachedMeasurementListItem[])
                : Array.isArray(rqCached)
                  ? rqCached
                  : [];

              const cachedMatch = serverList.length
                ? serverList.find((m) => {
                    // Preferuj porównanie po typeOfMeasurementUuid jeśli w cache jest dostępny,
                    // a jeśli nie, to po typeOfMeasurement (string).
                    if (m?.typeOfMeasurementUuid && typeOfMeasurementUuid) {
                      return m.typeOfMeasurementUuid === typeOfMeasurementUuid;
                    }
                    return m?.typeOfMeasurement === measurementType.type;
                  })
                : undefined;

              if (cachedMatch?.uuid) {
                const pageCountExisting =
                  typeof cachedMatch.pageCount === 'number' && cachedMatch.pageCount > 0
                    ? cachedMatch.pageCount
                    : getMeasurementPageCount(
                        cachedMatch.typeOfMeasurement ?? measurementType.type
                      );

                const isFinished = Boolean((cachedMatch as any).isFinished);

                // Jeśli online pomiar tego typu jest ukończony, tworzymy OFFLINE nowy pomiar-copy
                // z nowym uuid i pustym ostatnim krokiem.
                if (isFinished) {
                  const newUuid = tempId;
                  const lastFinishedPage = Math.max(1, pageCountExisting - 1);

                  const newMeasurement = {
                    id: null,
                    uuid: newUuid,
                    typeOfMeasurementId: null,
                    orderId: null,
                    userId: null,
                    createdAt: now,
                    updatedAt: now,
                    isCopy: 1,
                    isFinished: 0,
                    typeOfMeasurement: cachedMatch.typeOfMeasurement ?? measurementType.type,
                    measurementLabel: cachedMatch.measurementLabel ?? measurementType.label,
                    roomName: null,
                    pageCount: pageCountExisting,
                    lastFinishedPage,
                    orderUuid,
                    isOfflineOnly: true,
                  };

                  addOfflineMeasurement(newMeasurement as any);

                  return {
                    status: 'success',
                    message: 'Measurement created',
                    measurement: {
                      ...newMeasurement,
                      isCopy: true,
                    },
                  };
                }

                // Jeśli online pomiar nie jest ukończony, traktujemy to jak kontynuację.
                return {
                  status: 'success',
                  message: 'Measurement created',
                  measurement: {
                    ...cachedMatch,
                    uuid: cachedMatch.uuid,
                    typeOfMeasurement: cachedMatch.typeOfMeasurement ?? measurementType.type,
                    typeOfMeasurementUuid:
                      cachedMatch.typeOfMeasurementUuid ?? typeOfMeasurementUuid,
                    lastFinishedPage: cachedMatch.lastFinishedPage ?? 1,
                    isCopy: true,
                  },
                };
              }
            }

            const typeOfMeasurement = measurementType?.type ?? null;
            const pageCount = getMeasurementPageCount(typeOfMeasurement);

            const simulatedResponse = {
              status: 'success',
              message: 'Measurement created (offline)',
              measurement: {
                id: null,
                uuid: tempId,
                typeOfMeasurementId: null,
                orderId: null,
                userId: null,
                createdAt: now,
                updatedAt: now,
                isCopy: false,
                isFinished: 0,
                measurementClientInfo: null,
                measurementInstallationInfo: null,
                measurementInfo: null,
                typeOfMeasurement,
                measurementLabel: measurementType?.label ?? null,
                roomName: null,
                typeOfMeasurementUuid: typeOfMeasurementUuid ?? null,
                orderUuid: orderUuid ?? null,
                pageCount,
                lastFinishedPage: 1,
              },
            };

            if (orderUuid && measurementType) {
              addOfflineMeasurement({
                id: null,
                uuid: tempId,
                typeOfMeasurementId: null,
                orderId: null,
                userId: null,
                createdAt: now,
                updatedAt: now,
                isCopy: 0,
                isFinished: 0,
                typeOfMeasurement: measurementType.type,
                measurementLabel: measurementType.label,
                roomName: null,
                pageCount,
                lastFinishedPage: 1,
                orderUuid,
                isOfflineOnly: true,
              });
            }

            return simulatedResponse;
          },
        }
      );
    },
    onMutate: (variables) => {
      if (options && options.onMutate) {
        return options.onMutate(variables);
      }
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['measurements'] });

      const orderUuid = variables.orderUuid ?? null;
      if (orderUuid) {
        queryClient.setQueryData(['measurements', orderUuid], (prev: any) => {
          const prevMeasurements = Array.isArray(prev) ? prev : [];

          const newUuid = data?.measurement?.uuid as string | undefined;
          const offlineItem = newUuid ? getOfflineMeasurementByUuid(newUuid) : null;

          const nextItem = {
            uuid: newUuid,
            typeOfMeasurement:
              (offlineItem?.typeOfMeasurement ?? data?.measurement?.typeOfMeasurement) || '',
            isFinished: Boolean(offlineItem?.isFinished ?? data?.measurement?.isFinished),
            measurementLabel: offlineItem?.measurementLabel ?? '',
            roomName: offlineItem?.roomName || '',
            pageCount: offlineItem?.pageCount ?? data?.measurement?.pageCount,
            lastFinishedPage:
              offlineItem?.lastFinishedPage ?? data?.measurement?.lastFinishedPage,
          };

          const withoutDupes = prevMeasurements.filter(
            (m: any) => m?.uuid && m.uuid !== nextItem.uuid
          );

          return nextItem.uuid ? [...withoutDupes, nextItem] : withoutDupes;
        });

        queryClient.invalidateQueries({ queryKey: ['measurements', orderUuid] });
      }
      if (options && options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error:any, variables, context) => {
      if (options && options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
};

export default useStartMeasurement;
